import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree, Route } from '@angular/router';
import { SessionStorageService } from 'src/common/session-storage.service';

@Injectable()
export class AdminLoginGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly router: Router,
    private readonly sessionStorageService: SessionStorageService
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean | UrlTree {
    return this.resolveAdminAccess(state);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean | UrlTree {
    return this.resolveAdminAccess(state);
  }

  /**
   * Login routes are allowed while logged out.
   * Protected Admin routes require Admin authentication.
   * Redirects use a UrlTree only — never router.navigate() plus another result.
   */
  private resolveAdminAccess(state: RouterStateSnapshot): boolean | UrlTree {
    try {
      // New tab: sessionStorage is empty; Admin auth lives in localStorage.
      this.sessionStorageService.restoreAdminSessionFromLocalStorage();

      const requestedPath = this.getRequestedPath(state);
      const loginRoute = this.isAdminLoginPath(requestedPath);
      const authenticated = this.isAdminAuthenticated();

      if (loginRoute) {
        if (!authenticated) {
          return true;
        }
        return this.router.parseUrl(this.consumePostLoginTarget());
      }

      if (authenticated) {
        return true;
      }

      if (this.isProtectedAdminPath(requestedPath)) {
        this.sessionStorageService.setAdminReturnUrl(requestedPath);
      }
      return this.router.parseUrl('/admin/login');
    } catch (error) {
      console.error('AdminLoginGuard error:', error);
      const fallbackPath = this.getRequestedPath(state);
      if (this.isAdminLoginPath(fallbackPath)) {
        return true;
      }
      if (this.isProtectedAdminPath(fallbackPath) && !this.isAdminAuthenticated()) {
        this.sessionStorageService.setAdminReturnUrl(fallbackPath);
        return this.router.parseUrl('/admin/login');
      }
      return true;
    }
  }

  consumePostLoginTarget(): string {
    const returnUrl = this.normalizePath(this.sessionStorageService.getAdminReturnUrl());
    this.sessionStorageService.clearAdminReturnUrl();
    if (this.isProtectedAdminPath(returnUrl)) {
      return returnUrl;
    }
    return this.getDefaultAdminDashboardUrl();
  }

  getDefaultAdminDashboardUrl(): string {
    return '/admin/' + this.resolveRedirectTool() + '/dashboard';
  }

  private isAdminAuthenticated(): boolean {
    const adminUserId = this.sessionStorageService.getAdminUserId();

    return !!adminUserId && adminUserId !== 0;
  }

  /**
   * HashLocationStrategy: RouterStateSnapshot.url is already the Angular route
   * (e.g. /admin/PricingQuote/proposal/1246), not the full browser URL.
   * Do not prefer window.location.hash over state.url — during a redirect to
   * /admin/login the hash can still be the previous protected URL, which would
   * treat login as a protected route and redirect to login again (infinite loop).
   */
  private getRequestedPath(state: RouterStateSnapshot): string {
    let url = state && state.url ? state.url : '';
    if (!url || url === '/') {
      url = this.readHashPath();
    }
    return this.normalizePath(url);
  }

  private readHashPath(): string {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }
    let hash = window.location.hash || '';
    if (hash.charAt(0) === '#') {
      hash = hash.substring(1);
    }
    return hash;
  }

  private normalizePath(url: string): string {
    let path = (url || '').replace(/^#/, '').split('?')[0].split(';')[0];
    if (path && path.charAt(0) !== '/') {
      path = '/' + path;
    }
    if (path.length > 1 && path.charAt(path.length - 1) === '/') {
      path = path.substring(0, path.length - 1);
    }
    return path || '/';
  }

  private isAdminPath(path: string): boolean {
    return path === '/admin' || path.indexOf('/admin/') === 0;
  }

  private isAdminLoginPath(path: string): boolean {
    return path === '/admin' || path === '/admin/login';
  }

  private isProtectedAdminPath(path: string): boolean {
    return this.isAdminPath(path) && !this.isAdminLoginPath(path);
  }

  /**
   * Default Admin landing is /admin/{tool}/dashboard, using the casing from
   * the existing Admin child routes (e.g. Admin/dashboard, briefingServices/dashboard).
   * Lowercasing the stored tool name would miss those routes and hit ** → Front /login.
   */
  private resolveRedirectTool(): string {
    let tool = this.sessionStorageService.getUserLoginTool();
    if (!tool || tool.trim() === '') {
      const accessibleTools = this.sessionStorageService.getUserAccessibleTools();
      if (accessibleTools && accessibleTools.trim() !== '') {
        tool = accessibleTools.split(',')[0];
      }
    }

    if (!tool || tool.trim() === '') {
      tool = 'wireless';
    }

    tool = tool.trim();
    const lower = tool.toLowerCase();
    if (lower === 'actionitem') {
      tool = 'trac-it';
    } else if (lower === 'view 47') {
      tool = 'view47';
    }

    return this.matchAdminDashboardSegment(tool) || tool;
  }

  private matchAdminDashboardSegment(tool: string): string {
    const target = (tool + '/dashboard').toLowerCase();
    const configs: Route[] = this.router.config || [];

    for (let i = 0; i < configs.length; i++) {
      const route = configs[i];
      if (route.path !== 'admin' || !route.children) {
        continue;
      }

      for (let j = 0; j < route.children.length; j++) {
        const childPath = route.children[j].path || '';
        if (childPath.toLowerCase() === target) {
          const slash = childPath.lastIndexOf('/');
          return slash > 0 ? childPath.substring(0, slash) : childPath;
        }
      }
    }

    return '';
  }
}
